import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase/firestore";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { useAuthContext } from "../context/AuthContext";
import Animate from "../Components/Animate";
import Header from "../Components/Header";
import { Headphones, Play, Pause, CheckCircle, Volume2, FileAudio, AlertCircle, Lock } from 'lucide-react';
import KinescopePlayer from "../Components/KinescopePlayer";

// Импорт SVG иконок
import PdfIcon from "../assets/pdf-icon.svg";
import DownloadIcon from "../assets/download-icon.svg";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

// Функция для форматирования текста с Markdown
const formatDescription = (text) => {
  if (!text) return "";
  
  let formatted = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
  formatted = formatted.replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/g, '<u>$1</u>');
  formatted = formatted.replace(/^[•*]\s+(.*?)(?:\n|$)/gm, '<li>$1</li>');
  
  formatted = formatted.replace(/(<li>.*?<\/li>)\s*(?:(?!<li>)|$)/gs, (match) => {
    if (match.includes('</li><li>')) {
      return '<ul>' + match + '</ul>';
    }
    return match;
  });
  
  formatted = formatted.replace(/^(\d+)\.\s+(.*?)(?:\n|$)/gm, '<li value="$1">$2</li>');
  formatted = formatted.replace(/(<li value="\d+">.*?<\/li>)\s*(?:(?!<li>)|$)/gs, '<ol>$1</ol>');
  formatted = formatted.replace(/^###\s+(.*?)(?:\n|$)/gm, '<h3>$1</h3>');
  formatted = formatted.replace(/^##\s+(.*?)(?:\n|$)/gm, '<h2>$1</h2>');
  formatted = formatted.replace(/^#\s+(.*?)(?:\n|$)/gm, '<h1>$1</h1>');
  formatted = formatted.replace(/^>\s+(.*?)(?:\n|$)/gm, '<blockquote>$1</blockquote>');
  formatted = formatted.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline">$1</a>');
  formatted = formatted.replace(/^---$/gm, '<hr class="my-4 border-gray-600" />');
  
  const paragraphs = formatted.split('\n\n');
  formatted = paragraphs.map(p => {
    if (p.startsWith('<') && (p.includes('</h') || p.includes('</ul') || p.includes('</ol') || p.includes('<blockquote') || p.includes('<hr'))) {
      return p;
    }
    return p.trim() ? `<p>${p.replace(/\n/g, '<br />')}</p>` : '';
  }).join('');
  
  return formatted;
};

// Компонент для отображения форматированного описания
const FormattedDescription = ({ text, className = "" }) => {
  if (!text) return null;
  const formattedText = formatDescription(text);
  return (
    <div 
      className={`formatted-content ${className}`}
      dangerouslySetInnerHTML={{ __html: formattedText }}
    />
  );
};

// ===== АУДИОПЛЕЕР =====
const AudioItem = ({ title, url }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState("0:00");

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (!audio.duration) return;
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
    };

    const onLoadedMetadata = () => {
      const minutes = Math.floor(audio.duration / 60);
      const seconds = Math.floor(audio.duration % 60);
      setDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  return (
    <div className="bg-[#241352] border border-white/20 rounded-2xl px-4 py-3 flex items-center gap-3">
      <button
        onClick={togglePlay}
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity"
        style={{ background: '#6B3FD8' }}
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <rect x="1" y="1" width="4" height="12" rx="1" />
            <rect x="9" y="1" width="4" height="12" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <path d="M3 2L12 7L3 12V2Z" />
          </svg>
        )}
      </button>

      <div className="flex-1">
        <div className="text-sm font-medium" style={{ color: '#C8AFE8' }}>{title}</div>
        <div className="h-1 bg-white/20 rounded-full mt-2 overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${progress}%`, background: '#34D3FF' }}
          />
        </div>
      </div>

      <div className="text-xs opacity-70 min-w-[44px] text-right" style={{ color: '#8D85D5' }}>
        {duration}
      </div>

      <audio ref={audioRef} src={url} preload="metadata" />
    </div>
  );
};

// ===== КОМПОНЕНТ: Карточка видеоурока =====
const VideoLessonCard = ({ lesson, index, isActive, onClick, isCompleted, progress }) => {
  return (
    <div
      className={`
        relative
        flex
        gap-2
        p-4
        rounded-2xl
        bg-[#2E106D]
        border border-white/5
        backdrop-blur-sm
        cursor-pointer
        transition-all
        duration-300
        active:scale-[0.98]
        ${isActive ? "ring-1 ring-[#34D3FF]/40" : ""}
      `}
      onClick={onClick}
    >
      <div className="relative w-20 h-20 min-w-[80px] rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
        <img
          src={lesson.image || 'https://via.placeholder.com/160x160/2E106D/ffffff?text=Video'}
          className="w-full h-full object-cover"
          alt={`Часть ${index + 1}`}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/160x160/2E106D/ffffff?text=Video';
          }}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-white text-[14px] font-medium truncate">
              Часть {index + 1}: {lesson.title}
            </div>
            <div className="text-[14px] text-[#8D85D5] mt-1 line-clamp-2">
              {lesson.description}
            </div>
          </div>

          <div className="flex flex-col items-end flex-shrink-0">
            <span className="text-[12px] font-medium text-[#AFADC9] whitespace-nowrap">
              {isCompleted ? "Просмотрено" : "Видеоурок"}
            </span>
          </div>
        </div>

        {isActive && !isCompleted && (
          <div className="text-[12px] text-[#34D3FF] font-medium mt-2">
            Сейчас смотрите
          </div>
        )}

        <div className="mt-4">
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500 rounded-full bg-[#34D3FF]"
              style={{
                width: isCompleted ? "100%" : isActive ? `${progress}%` : "0%",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== ОСНОВНОЙ КОМПОНЕНТ =====
const LessonPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Получаем данные из контекстов
  const { 
    id: userId, 
    loading: userLoading,
    userType,
    premium: userIsPremium
  } = useUser();
  
  const { webUser } = useAuthContext();

  // Состояния
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  const [contentData, setContentData] = useState(null);
  const [contentTypeData, setContentTypeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Telegram WebApp инициализация
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.onClick(() => {
        navigate(-1);
      });
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        window.Telegram.WebApp.BackButton.isVisible = false;
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate, userType]);

  const [training, setTraining] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [audioLessons, setAudioLessons] = useState([]);
  const [activeTab, setActiveTab] = useState("steps");
  const [activeLessonIndex, setActiveLessonIndex] = useState(0);
  const [expandedTrainingDescription, setExpandedTrainingDescription] = useState(false);

  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [videoProgress, setVideoProgress] = useState(0);

  const descriptionRef = useRef(null);

  const BASE_URL = "https://artdownload2-production.up.railway.app";

  const totalLessons = lessons.length;
  const isSingleLesson = totalLessons === 1 && audioLessons.length === 0;

  const getCollectionName = () => {
    if (userType === 'web') {
      return 'webUsers';
    }
    return 'telegramUsers';
  };

  const getUserEmail = () => {
    if (userType === 'web' && webUser?.email) {
      return webUser.email;
    }
    return null;
  };

  // Проверка срока действия доступа
  const isAccessExpired = (accessArray, itemId) => {
    const access = accessArray.find(a => a.id === itemId);
    if (!access || !access.endDate) return false;
    return new Date(access.endDate) < new Date();
  };

  // Поиск родительского курса для урока или тренинга
  const findParentCourse = async (itemId, itemType) => {
    try {
      const coursesSnap = await getDocs(collection(db, "courses"));
      
      for (const courseDoc of coursesSnap.docs) {
        const courseData = courseDoc.data();
        
        // Проверяем в уроках курса (массив lessons)
        if (courseData.lessons && courseData.lessons.includes(itemId)) {
          return { id: courseDoc.id, title: courseData.title, type: 'lesson' };
        }
        
        // Проверяем в тренингах курса
        if (courseData.trainings && courseData.trainings.includes(itemId)) {
          return { id: courseDoc.id, title: courseData.title, type: 'training' };
        }
        
        // Проверяем в блоках курса
        if (courseData.blocks) {
          for (const block of courseData.blocks) {
            if (block.contents) {
              const hasItem = block.contents.some(content => 
                content.type === itemType && content.data?.id === itemId
              );
              if (hasItem) {
                return { id: courseDoc.id, title: courseData.title, type: itemType };
              }
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error finding parent course:", error);
      return null;
    }
  };

  // Загрузка контента и проверка доступа
  const loadContentAndCheckAccess = async () => {
    try {
      setIsLoading(true);
      
      // Загружаем данные пользователя
      let userTrainings = [];
      let userTrainingsAccess = [];
      let userCourses = [];
      let userCoursesAccess = [];
      let giftTimers = {};
      
      if (userId) {
        const collectionName = getCollectionName();
        const userDocRef = doc(db, collectionName, userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userTrainings = userData.trainings || [];
          userTrainingsAccess = userData.trainingsAccess || [];
          userCourses = userData.courses || [];
          userCoursesAccess = userData.coursesAccess || [];
          
          console.log(`📚 Тренинги пользователя:`, userTrainings);
          console.log(`📚 Курсы пользователя:`, userCourses);
        }
        
        // Загружаем подарки
        const giftsRef = collection(db, collectionName, userId, "gifts");
        const q = query(giftsRef, where("status", "==", "active"));
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
          if (expiresAt > new Date()) {
            giftTimers[data.itemId] = true;
          }
        });
      }
      
      // Функция проверки доступа для элемента
      const checkItemAccess = (item, parentCourseId = null) => {
        if (!item) return { hasAccess: false, reason: 'no_content' };

        console.log(`🔍 Проверка доступа к: ${item.title || item.id}`, {
          userId,
          itemId: item.id,
          parentCourseId,
          userCourses,
          userTrainings
        });

        // 1. Подарок
        if (userId && giftTimers[item.id]) {
          return { hasAccess: true, reason: 'gift' };
        }

        // 2. Если указан родительский курс, проверяем доступ к нему
        if (parentCourseId && userId && userCourses.includes(parentCourseId)) {
          const isExpired = isAccessExpired(userCoursesAccess, parentCourseId);
          if (!isExpired) {
            console.log(`✅ Доступ через родительский курс: ${parentCourseId}`);
            return { hasAccess: true, reason: 'parent_course' };
          }
        }

        // 3. Прямой доступ к тренингу
        if (userId && userTrainings.includes(item.id)) {
          const isExpired = isAccessExpired(userTrainingsAccess, item.id);
          if (!isExpired) {
            return { hasAccess: true, reason: 'user_training' };
          }
        }

        // 4. Прямой доступ к курсу
        if (userId && userCourses.includes(item.id)) {
          const isExpired = isAccessExpired(userCoursesAccess, item.id);
          if (!isExpired) {
            return { hasAccess: true, reason: 'user_course' };
          }
        }

        // 5. Premium
        if (item.premium && userIsPremium) {
          return { hasAccess: true, reason: 'premium' };
        }

        // 6. Бесплатный контент (только если есть авторизация)
        if (userId && !item.premium && !item.paidAccessId && !item.paidAccessEmail) {
          return { hasAccess: true, reason: 'free' };
        }

        return { hasAccess: false, reason: 'no_access' };
      };
      
      // Сначала пробуем загрузить как урок (masterClass)
      const lessonRef = doc(db, "masterClasses", id);
      const lessonSnap = await getDoc(lessonRef);
      
      if (lessonSnap.exists()) {
        const lessonData = {
          id: lessonSnap.id,
          ...lessonSnap.data(),
          type: 'lesson'
        };
        
        // Ищем родительский курс для этого урока
        const parentCourse = await findParentCourse(id, 'lesson');
        const result = checkItemAccess(lessonData, parentCourse?.id);
        
        setHasAccess(result.hasAccess);
        setAccessReason(result.reason);
        setAccessChecked(true);
        
        if (result.hasAccess) {
          setContentData(lessonData);
          setContentTypeData('lesson');
        }
        setIsLoading(false);
        return;
      }
      
      // Пробуем загрузить как тренинг
      const trainingRef = doc(db, "trainings", id);
      const trainingSnap = await getDoc(trainingRef);
      
      if (trainingSnap.exists()) {
        const trainingData = {
          id: trainingSnap.id,
          ...trainingSnap.data(),
          type: 'training'
        };
        
        // Ищем родительский курс для этого тренинга
        const parentCourse = await findParentCourse(id, 'training');
        const result = checkItemAccess(trainingData, parentCourse?.id);
        
        setHasAccess(result.hasAccess);
        setAccessReason(result.reason);
        setAccessChecked(true);
        
        if (!result.hasAccess) {
          setIsLoading(false);
          return;
        }
        
        setContentData(trainingData);
        setContentTypeData('training');
        setTraining(trainingData);
        
        // Загружаем уроки тренинга
        if (trainingData.lessons?.length) {
          const loadedLessons = [];
          for (const lessonId of trainingData.lessons) {
            const lessonRef = doc(db, "masterClasses", lessonId);
            const lessonSnap = await getDoc(lessonRef);
            if (lessonSnap.exists()) {
              loadedLessons.push({ id: lessonSnap.id, ...lessonSnap.data() });
            }
          }
          setLessons(loadedLessons);
          setActiveLessonIndex(0);
        }

        // Загружаем аудиоуроки
        if (trainingData.audioLessons?.length) {
          const loadedAudioLessons = [];
          for (const audioId of trainingData.audioLessons) {
            const audioRef = doc(db, "audioLessons", audioId);
            const audioSnap = await getDoc(audioRef);
            if (audioSnap.exists()) {
              const data = audioSnap.data();
              let audioFiles = [];
              if (data.audioFiles && Array.isArray(data.audioFiles)) {
                audioFiles = data.audioFiles.map(audio => {
                  if (typeof audio === 'string') {
                    return { name: `Аудио`, url: audio.startsWith('/') ? `${BASE_URL}${audio}` : audio };
                  } else if (audio && audio.url) {
                    return { ...audio, url: audio.url.startsWith('/') ? `${BASE_URL}${audio.url}` : audio.url };
                  }
                  return audio;
                });
              } else if (data.audioUrl) {
                audioFiles = [{
                  name: data.title || 'Аудиоурок',
                  url: data.audioUrl.startsWith('/') ? `${BASE_URL}${data.audioUrl}` : data.audioUrl,
                  duration: data.duration || ''
                }];
              }
              loadedAudioLessons.push({ id: audioSnap.id, ...data, audioFiles });
            }
          }
          setAudioLessons(loadedAudioLessons);
        }
        
        setIsLoading(false);
        return;
      }
      
      // Пробуем загрузить как курс
      const courseRef = doc(db, "courses", id);
      const courseSnap = await getDoc(courseRef);
      
      if (courseSnap.exists()) {
        const courseData = {
          id: courseSnap.id,
          ...courseSnap.data(),
          type: 'course'
        };
        
        const result = checkItemAccess(courseData);
        setHasAccess(result.hasAccess);
        setAccessReason(result.reason);
        setAccessChecked(true);
        
        if (result.hasAccess) {
          setContentData(courseData);
          setContentTypeData('course');
        }
        setIsLoading(false);
        return;
      }
      
      console.error("Контент не найден:", id);
      setAccessChecked(true);
      setHasAccess(false);
      setIsLoading(false);
      
    } catch (e) {
      console.error("Ошибка загрузки контента", e);
      setAccessChecked(true);
      setHasAccess(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!userLoading) {
      loadContentAndCheckAccess();
    }
  }, [id, userId, userType, userLoading]);

  useEffect(() => {
    const saved = localStorage.getItem(`training_progress_${id}`);
    if (saved) {
      setCompletedLessons(new Set(JSON.parse(saved)));
    }
  }, [id]);

  const saveProgress = (lessonId) => {
    const updated = new Set(completedLessons);
    updated.add(lessonId);
    setCompletedLessons(updated);
    localStorage.setItem(`training_progress_${id}`, JSON.stringify([...updated]));
  };

  const handleVideoProgress = (progress) => {
    setVideoProgress(progress);
    const currentLessonId = lessons[activeLessonIndex]?.id;
    if (progress > 90 && currentLessonId && !completedLessons.has(currentLessonId)) {
      saveProgress(currentLessonId);
    }
  };

  useEffect(() => {
    if (!lessons.length || activeLessonIndex === -1 || !hasAccess) return;
    
    const currentLessonId = lessons[activeLessonIndex]?.id;
    if (completedLessons.has(currentLessonId)) return;

    const timer = setInterval(() => {
      setVideoProgress((p) => {
        const next = Math.min(p + 10, 100);
        handleVideoProgress(next);
        if (next === 100) clearInterval(timer);
        return next;
      });
    }, 3000);

    return () => clearInterval(timer);
  }, [lessons, activeLessonIndex, completedLessons, hasAccess]);

  const getLessonStatus = (lessonId) => ({
    isCompleted: completedLessons.has(lessonId),
  });

  const handleLessonClick = (index) => {
    setActiveLessonIndex(index);
    setVideoProgress(0);
  };

  const activeLesson = lessons[activeLessonIndex];
  const activeLessonCompleted = activeLesson ? completedLessons.has(activeLesson.id) : false;

  const getAccessMessage = () => {
    if (!userId) return "Для просмотра материала требуется авторизация";
    if (accessReason === 'gift') return "🎁 Доступ предоставлен по подарку";
    if (accessReason === 'user_training') return "👤 Индивидуальный доступ к тренингу";
    if (accessReason === 'user_course') return "👤 Индивидуальный доступ к курсу";
    if (accessReason === 'parent_course') return "👤 Доступ через курс";
    if (accessReason === 'premium') return "⭐ Premium доступ";
    if (accessReason === 'free') return "🆓 Бесплатный материал";
    return "У вас нет доступа к этому материалу";
  };

  const renderNoAccess = () => {
    return (
      <div className="min-h-screen w-full text-white pb-28">
        <Header />
        <div className="flex items-center justify-center h-64 px-4">
          <div className="text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-white text-xl font-medium mb-2">Доступ запрещен</p>
            <p className="text-white/60 mb-6">{getAccessMessage()}</p>
            <button 
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-colors"
            >
              Вернуться назад
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSingleLesson = () => {
    if (!contentData || contentTypeData !== 'lesson') return null;
    const content = contentData;
    
    return (
      <>
        <div className="px-4 pt-4">
          <div className="w-full rounded-[24px] overflow-hidden bg-black aspect-video relative">
            {content.videoUrl ? (
              <KinescopePlayer videoUrl={content.videoUrl} title={content.title} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/60">Видео не найдено</div>
            )}
          </div>
        </div>

        {accessReason && accessReason !== 'no_access' && (
          <div className="px-4 mt-3">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
              {getAccessMessage()}
            </span>
          </div>
        )}

        <div className="px-4 mt-4">
          <h1 className="text-[22px] font-bold">{content.title}</h1>
        </div>

        {content.description && (
          <div className="px-4 mt-4">
            <div className="bg-[#2A1460] rounded-[20px] p-4">
              <FormattedDescription text={content.description} className="text-white/80 text-[15px] leading-relaxed" />
            </div>
          </div>
        )}

        {content.audioFiles && content.audioFiles.length > 0 && (
          <div className="px-4 mt-4">
            <h3 className="text-[18px] font-medium text-white flex items-center gap-2 mb-3">
              <FileAudio className="w-5 h-5 text-green-400" /> Аудио материалы
            </h3>
            {content.audioFiles.map((file, index) => (
              <AudioItem key={index} title={file.name || `Аудио ${index + 1}`} url={file.url} />
            ))}
          </div>
        )}

        {content.files && content.files.length > 0 && (
          <div className="px-4 mt-4">
            <h3 className="text-[16px] font-medium text-white mb-3">Материалы урока</h3>
            {content.files.map((file, index) => (
              <a key={index} href={file.url} target="_blank" rel="noopener noreferrer" className="bg-[#2A1460] rounded-[18px] p-4 flex items-center justify-between hover:bg-[#3A1E70] transition-colors group mb-2">
                <div className="flex items-center gap-3">
                  <img src={file.type === "application/pdf" ? PdfIcon : DownloadIcon} alt="Файл" className="w-7 h-7" />
                  <span className="text-[15px] group-hover:text-white transition-colors">{file.name || "Файл"}</span>
                </div>
                <img src={DownloadIcon} alt="Скачать" className="w-7 h-7 opacity-70 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderTraining = () => {
    if (!training) return null;
    
    return (
      <>
        <div className="px-4 pt-4">
          {activeLesson ? (
            <div className="w-full rounded-[24px] overflow-hidden bg-black aspect-video relative">
              {activeLesson.videoUrl ? (
                <KinescopePlayer videoUrl={activeLesson.videoUrl} title={activeLesson.title} />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/60">Видео не найдено</div>
              )}
            </div>
          ) : (
            <div className="w-full rounded-[24px] aspect-video flex items-center justify-center bg-gray-800 text-white/60">
              Нет видеоуроков
            </div>
          )}

          {activeLessonCompleted && (
            <div className="mt-4 p-3 rounded-[16px] bg-green-900/30 border border-green-500/30 text-center">
              <div className="text-green-400 text-[15px] font-medium flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" /> Урок завершён
              </div>
            </div>
          )}
        </div>

        {accessReason && accessReason !== 'no_access' && (
          <div className="px-4 mt-3">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
              {getAccessMessage()}
            </span>
          </div>
        )}

        <div className="px-4 mt-4">
          <h1 className="text-[22px] font-bold">{activeLesson ? activeLesson.title : training?.title}</h1>

          {totalLessons > 1 && (
            <div className="mt-3">
              <div className="flex justify-between text-[14px] mb-1">
                <span className="text-cyan-300">Прогресс тренинга</span>
                <span className="text-white/60">{completedLessons.size} / {totalLessons}</span>
              </div>
              <div className="h-[6px] bg-white/10 rounded-full overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${(completedLessons.size / totalLessons) * 100}%`, background: "linear-gradient(90deg, #10B981, #34D399, #8B5CF6)" }} />
              </div>
            </div>
          )}
        </div>

        {!isSingleLesson && (
          <>
            <div className="px-4 mt-5 flex gap-6 border-b border-white/10">
              <button onClick={() => setActiveTab("steps")} className={`pb-2 px-1 text-[18px] font-medium transition-colors relative ${activeTab === "steps" ? "text-pink-400" : "text-white/50 hover:text-white/70"}`}>
                Этапы ({totalLessons})
                {activeTab === "steps" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-400" />}
              </button>
              <button onClick={() => setActiveTab("materials")} className={`pb-2 px-1 text-[18px] font-medium transition-colors relative ${activeTab === "materials" ? "text-pink-400" : "text-white/50 hover:text-white/70"}`}>
                Материалы
                {activeTab === "materials" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-400" />}
              </button>
            </div>

            {activeTab === "steps" && (
              <div className="px-4 mt-4 flex flex-col gap-4">
                {lessons.map((lesson, index) => {
                  const { isCompleted } = getLessonStatus(lesson.id);
                  const isActive = index === activeLessonIndex;
                  return (
                    <VideoLessonCard
                      key={`video-${lesson.id}`}
                      lesson={lesson}
                      index={index}
                      isActive={isActive}
                      onClick={() => handleLessonClick(index)}
                      isCompleted={isCompleted}
                      progress={videoProgress}
                    />
                  );
                })}
              </div>
            )}

            {activeTab === "materials" && (
              <div className="px-4 mt-4 flex flex-col gap-4">
                {renderMaterials()}
              </div>
            )}
          </>
        )}

        {isSingleLesson && (
          <div className="px-4 mt-4 flex flex-col gap-4">
            {renderMaterials()}
          </div>
        )}
      </>
    );
  };

  const renderMaterials = () => {
    return (
      <>
        {training?.description && (
          <div className="bg-[#2A1460] rounded-[20px] p-4">
            <h3 className="text-[16px] font-medium mb-3 text-white">Описание тренинга</h3>
            <div ref={descriptionRef} className={`overflow-hidden transition-all duration-300 ${expandedTrainingDescription ? "max-h-[2000px]" : "max-h-[120px]"}`}>
              <FormattedDescription text={training.description} className="text-white/80 text-[15px] leading-relaxed" />
            </div>
            {training.description.length > 120 && (
              <button onClick={() => setExpandedTrainingDescription((v) => !v)} className="mt-3 text-[#34D3FF] text-[14px] font-medium hover:text-[#5FDBFF] transition-colors">
                {expandedTrainingDescription ? "Свернуть" : "Читать полностью"}
              </button>
            )}
          </div>
        )}

        {audioLessons.length > 0 && (
          <div className="flex flex-col gap-4">
            <h3 className="text-[18px] font-medium text-white flex items-center gap-2"><FileAudio className="w-5 h-5 text-green-400" /> Аудио материалы</h3>
            {audioLessons.map((audioLesson) => (
              <div key={audioLesson.id}>
                {audioLesson.audioFiles?.map((file, fileIndex) => (
                  <div key={file.filename || fileIndex} className="mb-2 last:mb-0">
                    <AudioItem title={file.name || `Аудио ${fileIndex + 1}`} url={file.url} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {training?.files?.length > 0 && (
          <div className="flex flex-col gap-3">
            <h3 className="text-[16px] font-medium text-white">Материалы тренинга</h3>
            {training.files.map((file, index) => (
              <a key={file.filename || index} href={file.url} target="_blank" rel="noopener noreferrer" className="bg-[#2A1460] rounded-[18px] p-4 flex items-center justify-between hover:bg-[#3A1E70] transition-colors group">
                <div className="flex items-center gap-3">
                  <img src={file.type === "application/pdf" ? PdfIcon : DownloadIcon} alt="Файл" className="w-7 h-7" />
                  <span className="text-[15px] group-hover:text-white transition-colors">{file.name || "Файл"}</span>
                </div>
                <img src={DownloadIcon} alt="Скачать" className="w-7 h-7 opacity-70 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        )}
      </>
    );
  };

  if (isLoading || userLoading) {
    return (
      <Animate>
        <div className="min-h-screen w-full text-white pb-28">
          <Header />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
              <div className="text-white text-lg">Загрузка...</div>
            </div>
          </div>
        </div>
      </Animate>
    );
  }

  if (!accessChecked) {
    return (
      <Animate>
        <div className="min-h-screen w-full text-white pb-28">
          <Header />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
              <div className="text-white text-lg">Проверка доступа...</div>
            </div>
          </div>
        </div>
      </Animate>
    );
  }

  if (!hasAccess) {
    return renderNoAccess();
  }

  if (!contentData) {
    return (
      <Animate>
        <div className="min-h-screen w-full text-white pb-28">
          <Header />
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-white text-lg">Контент не найден</p>
              <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-colors">Вернуться назад</button>
            </div>
          </div>
        </div>
      </Animate>
    );
  }

  return (
    <Animate>
      <div className="min-h-screen w-full text-white pb-28">
        <Header />
        {contentTypeData === 'lesson' ? renderSingleLesson() : renderTraining()}
      </div>
      <style jsx="true">{`
        .formatted-content { word-wrap: break-word; }
        .formatted-content h1 { font-size: 24px; font-weight: 700; margin-bottom: 16px; color: white; }
        .formatted-content h2 { font-size: 20px; font-weight: 600; margin-bottom: 12px; color: white; }
        .formatted-content h3 { font-size: 18px; font-weight: 600; margin-bottom: 10px; color: white; }
        .formatted-content p { margin-bottom: 12px; line-height: 1.6; color: rgba(255, 255, 255, 0.8); }
        .formatted-content strong { font-weight: 700; color: white; }
        .formatted-content em { font-style: italic; }
        .formatted-content u { text-decoration: underline; }
        .formatted-content ul, .formatted-content ol { margin: 12px 0; padding-left: 24px; }
        .formatted-content ul { list-style-type: disc; }
        .formatted-content ol { list-style-type: decimal; }
        .formatted-content li { margin-bottom: 4px; color: rgba(255, 255, 255, 0.8); }
        .formatted-content blockquote { margin: 16px 0; padding: 12px 16px; background: rgba(255, 255, 255, 0.05); border-left: 4px solid #34D3FF; border-radius: 0 8px 8px 0; color: rgba(255, 255, 255, 0.9); }
        .formatted-content hr { margin: 24px 0; border: 0; height: 1px; background: rgba(255, 255, 255, 0.1); }
        .formatted-content a { color: #34D3FF; text-decoration: underline; transition: color 0.2s; }
        .formatted-content a:hover { color: #5FDBFF; }
      `}</style>
    </Animate>
  );
};

export default LessonPage;